import React, { useState, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardText,
  Spinner,
  CardImg,
  Input,
  FormGroup,
  Label,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  CardHeader, // Import CardHeader for the Categories heading
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
} from "reactstrap"
import axios from "axios"
import classnames from "classnames"
import config from "config/config"
import yelpIcon from "../../assets/images/icons/yelp.svg"
import googleIcon from "../../assets/images/icons/google.png"
import healthGradeIcon from "../../assets/images/icons/healthgrade.png"
import "./index.css" // Assuming you have a CSS file for custom styles
import GoogleReviewModal from "components/GoogleReviewModal/GoogleReviewModal"
import SnackbarComponent from "components/SnackBar"
import PrimaryButton from "newcomponents/primary-button"
import { useSelector } from "react-redux"

const SurveyForm = () => {
  const { campaignId, organizationId, distributionId, surveyId, customerId } =
    useParams()
  const [customerSurveyAssignment, setCustomerSurveyAssignment] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [reviewRedirectionModalOpen, setReviewRedirectionModalOpen] =
    useState(false)
  const FALLBACK_PLACE_ID = "ChIJYyHdmkT92oAR0KLE-JwZcwM"
  const [googleReviewModalOpen, setGoogleReviewModalOpen] = useState(false)
  const [googleReviewUrl, setGoogleReviewUrl] = useState(null)
  const [reviewText, setReviewText] = useState("")
  const [survey, setSurvey] = useState(null)
  const [facebookReviewUrl, setFacebookReviewUrl] = useState(null)
  const [yelpReviewUrl, setYelpReviewUrl] = useState(null)
  const [responses, setResponses] = useState({})
  const [loading, setLoading] = useState(true)
  const [customer, setCustomer] = useState(true)
  const [activeTab, setActiveTab] = useState(0) // Track the active tab index
  const [submitted, setSubmitted] = useState(false)
  const [available, setAvailable] = useState(true)
  const [started, setStarted] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [copySuccess, setCopySuccess] = useState(null)
  const mode = useSelector(state => state.Layout.layoutMode)
  const isDark = mode === "dark"
  const hiddenTextareaRef = useRef(null)

  const toggleReviewRedirectionModal = () =>
    setReviewRedirectionModalOpen(!reviewRedirectionModalOpen)

  const markSurveyOpened = async () => {
    try {
      await axios.put(
        `${config.backendUrl}/survey/markSurveyOpened/${distributionId}/${surveyId}/${customerId}`,
        undefined,
        { skipLoader: true },
      )
    } catch (err) {
      console.log(err)
    }
  }

  const getGoogleReviewUrl = org => {
    const pid = org?.googlePlaceId
    if (pid) return `https://search.google.com/local/writereview?placeid=${pid}`
    return org?.GoogleIntegration?.profileUrl || null
  }

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/organization/getOrganization?orgId=${organizationId}`,
        { skipLoader: true },
      )
      const org = response.data.organization
      setOrganization({
        ...org,
        // Normalize integrations to the singular keys used throughout the UI
        FacebookIntegration:
          org.FacebookIntegration || org.FacebookIntegrations?.[0] || null,
        YelpIntegration: org.YelpIntegration || org.YelpIntegrations?.[0] || null,
        HealthgradeIntegration:
          org.HealthgradeIntegration || org.HealthgradeIntegrations?.[0] || null,
      })
      setLoading(false) // Organization data loaded
    } catch (err) {
      setLoading(false) // Stop loading in case of error
      console.log(err)
    }
  }

  const fetchCustomerSurveyAssignment = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/customerDistributionStatus/${distributionId}/${surveyId}/${customerId}`,
        { skipLoader: true },
      )
      setCustomerSurveyAssignment(response.data.assignment)
      if (response.data.assignment.Opened === false) {
        markSurveyOpened()
      }
      if (response.data.assignment.Responded) {
        setSubmitted(true)
      }
    } catch (err) {
      console.log(err)
    }
  }

  const fetchSurvey = async () => {
    try {
      const response = await axios.get(
        `${config.backendUrl}/survey/${surveyId}`,
        { skipLoader: true },
      )
      setSurvey(response.data.survey)
    } catch (err) {
      console.log(err)
    }
  }

  const handleInputChange = (categoryIndex, questionIndex, value) => {
    const key = `category${categoryIndex}_question${questionIndex}`
    setResponses({ ...responses, [key]: value })
  }

  const fetchFacebookPageUrl = async () => {
    try {
      const response = await axios.get(
        `https://graph.facebook.com/v20.0/${organization.FacebookIntegration.pageId}?fields=name&access_token=${organization.FacebookIntegration.pageToken}`,
        { skipLoader: true },
      )

      const pageName = response.data.name.toLowerCase().replace(/\s+/g, "")

      const facebookReviewUrl = `https://www.facebook.com/${pageName}/reviews/`

      // Set the URL
      setFacebookReviewUrl(facebookReviewUrl)
    } catch (err) {
      console.log(err)
    }
  }

  const copyReviewTextToClipboard = async value => {
    if (!value) return false
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value)
        return true
      }
    } catch {}
    try {
      if (hiddenTextareaRef.current) {
        hiddenTextareaRef.current.value = value
        hiddenTextareaRef.current.select()
        return document.execCommand("copy")
      }
    } catch {}
    return false
  }

  const handlePlatformClick = async targetUrl => {
    if (!targetUrl) return
    const copied = await copyReviewTextToClipboard(reviewText)
    if (copied) {
      setCopySuccess("Text has been automatically copied.")
      setTimeout(() => setCopySuccess(null), 2000)
    }
    setTimeout(() => {
      window.open(targetUrl, "_blank", "noopener,noreferrer")
    }, 300)
  }

  const handleSubmit = async () => {
    // debugger
    // Prepare the responses in the desired format
    const formattedResponses = {
      surveyId: surveyId, // This should be the current survey ID
      customerId: customerId, // Make sure this is the correct customer ID
      distributionId: distributionId,
      responses: Object.keys(responses).map(key => {
        const [categoryIndex, questionIndex] = key.match(/\d+/g)
        const questionId =
          survey.SurveyCategories[categoryIndex].SurveyQuestions[questionIndex]
            .id
        return {
          questionId: questionId,
          responseValue: responses[key], // Use the response value directly
        }
      }),
    }

    console.log("hello", formattedResponses)

    try {
      const response = await axios.post(
        `${config.backendUrl}/survey/submitResponse`,
        formattedResponses,
        { skipLoader: true },
      )
      console.log("Survey submitted successfully", response.data)
      setReviewText(response.data?.reviewText || "")
      setSubmitted(true)

      if (response.data.score) {
        const score =
          typeof response.data?.score === "string"
            ? parseFloat(response.data.score)
            : response.data?.score

        if (Number.isFinite(score) && score > survey.threshold) {
          console.log("Score greater than threshold")
          let nextGoogleUrl = null
          if (customerSurveyAssignment.for === "org") {
            nextGoogleUrl = getGoogleReviewUrl(organization)
          } else {
            console.log("inside doctor for")
            const doctorResponse = await axios.get(
              `${config.backendUrl}/doctors/${customerSurveyAssignment.doctorId}`,
              { skipLoader: true },
            )

            console.log("Doctor", doctorResponse)

            nextGoogleUrl = doctorResponse.data.doctor.googleReviewLink
            console.log("URL", nextGoogleUrl)
          }
          setGoogleReviewUrl(nextGoogleUrl || null)
          if (organization?.FacebookIntegration) {
            await fetchFacebookPageUrl()
          }
          const hasAnyReviewLink =
            nextGoogleUrl ||
            organization?.FacebookIntegration ||
            organization?.YelpIntegration ||
            organization?.HealthgradeIntegration
          if (hasAnyReviewLink) {
            setReviewRedirectionModalOpen(true)
            setGoogleReviewModalOpen(false) // avoid stacking
          }
        }
      }
      // fetchFacebookPageUrl()
      // toggleReviewRedirectionModal()
    } catch (err) {
      console.log("Error submitting survey", err)
    }
  }

  const fetchCampaign = async () => {
    try {
      const campaignResponse = await axios.get(
        `${config.backendUrl}/survey/getCampaign?distributionId=${campaignId}`,
        { skipLoader: true },
      )

      if (campaignResponse.data.campaign.status !== "active") {
        console.log("Active")
        setAvailable(false)
      }

      console.log("Hellohhhh", campaignResponse)
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    fetchCampaign()
    fetchCustomerSurveyAssignment()
    fetchOrganization()
    fetchSurvey()
  }, [])

  // Flattened so the slideshow can step through every question in order,
  // regardless of which category it's nested under.
  const flatQuestions = (survey?.SurveyCategories || []).flatMap(
    (category, categoryIndex) =>
      (category.SurveyQuestions || []).map((question, questionIndex) => ({
        category,
        categoryIndex,
        question,
        questionIndex,
      })),
  )
  const isLastQuestion = currentIndex === flatQuestions.length - 1

  const goToNextQuestion = () =>
    setCurrentIndex(i => Math.min(i + 1, flatQuestions.length - 1))
  const goToPreviousQuestion = () => setCurrentIndex(i => Math.max(i - 1, 0))

  return (
    <div
      className="survey-page-bg p-3 p-md-4"
      style={{
        backgroundImage: `url('/circle1.svg'), url('/circle2.svg')`,
      }}
    >
      <div className="survey-shell">
        {submitted ? (
          <Card className="text-center br shadow-lg border-0">
            <CardBody className="p-4 p-md-5">
              <div
                className="mx-auto mb-3"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "#e8f6ee",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="mdi mdi-check-bold"
                  style={{ fontSize: 28, color: "#22c55e" }}
                />
              </div>
              <CardTitle tag="h5" className="font-weight-bold">
                Survey Submitted
              </CardTitle>
              <CardText className="text-muted mb-0">
                Thank you for your feedback!
              </CardText>
            </CardBody>
          </Card>
        ) : !available ? (
          <Card className="text-center br shadow-lg border-0">
            <CardBody className="p-4 p-md-5">
              <CardTitle tag="h5" className="font-weight-bold">
                Survey Unavailable
              </CardTitle>
              <CardText className="text-muted mb-0">
                The survey is currently unavailable.
              </CardText>
            </CardBody>
          </Card>
        ) : !started ? (
          <Card className="shadow-lg border-0 br">
            <CardBody className="text-center p-4 p-md-5">
              {loading ? (
                <Spinner color="primary" />
              ) : (
                <>
                  {/* Display Organization Logo */}
                  {organization && organization.logo && (
                    <CardImg
                      top
                      src={organization.logo}
                      alt={`${organization.name} Logo`}
                      style={{
                        maxWidth: "150px",
                        marginBottom: "20px",
                        borderRadius: "10px",
                      }}
                    />
                  )}

                  {/* Display Organization Name */}
                  <CardTitle tag="h4" className="mb-3 font-weight-bold">
                    {organization ? organization.name : "Organization Name"}
                  </CardTitle>

                  {/* Welcome Message */}
                  <CardText className="lead">
                    {organization
                      ? `Welcome to ${organization.name}. We value your feedback and encourage you to take your time in completing this survey.`
                      : "Loading organization details..."}
                  </CardText>

                  {/* Organization Description */}
                  <CardText className="text-muted">
                    {organization && organization.description
                      ? organization.description
                      : "We strive to enhance our services with your valuable insights. Feel free to reach out for any queries or support."}
                  </CardText>

                  <div className="mt-4">
                    <PrimaryButton
                      type="button"
                      disabled={!survey}
                      onClick={() => {
                        setCurrentIndex(0)
                        setStarted(true)
                      }}
                      style={{ padding: "12px 40px", borderRadius: "8px" }}
                    >
                      Start Survey
                    </PrimaryButton>
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        ) : (
          <>
            {/* Display Survey Title and Description */}
            {survey && (survey.title || survey.description) && (
              <div className="text-center mb-5">
                {survey.title && (
                  <h1
                    className="mb-2"
                    style={{
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "clamp(28px, 5vw, 42px)",
                      lineHeight: 1.2,
                      textShadow: "0 1px 4px rgba(0,0,0,0.25)",
                    }}
                  >
                    {survey.title}
                  </h1>
                )}
                {survey.description && (
                  <p
                    className="mb-0 mx-auto"
                    style={{
                      color: "#fff",
                      opacity: 0.95,
                      maxWidth: "620px",
                      fontSize: "17px",
                      textShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  >
                    {survey.description}
                  </p>
                )}
              </div>
            )}

            {/* One question at a time, slideshow-style - see the floating
                bar below for Back/Next/Submit and position progress. */}
            {flatQuestions.length > 0 &&
              (() => {
                const { category, categoryIndex, question, questionIndex } =
                  flatQuestions[currentIndex]
                const responseKey = `category${categoryIndex}_question${questionIndex}`

                return (
                  <Card
                    key={currentIndex}
                    className="mb-3 br shadow-lg border-0 survey-slide"
                    style={{ backgroundColor: isDark ? "#252F40" : "#fff" }}
                  >
                    <CardBody
                      className="text-center p-4 p-md-5 d-flex flex-column justify-content-center"
                      style={{ minHeight: "260px" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <small
                          className="text-muted text-uppercase"
                          style={{ fontWeight: 600, letterSpacing: "0.04em" }}
                        >
                          Question {currentIndex + 1} of {flatQuestions.length}
                        </small>
                        <span className="survey-category-badge">
                          {category.name}
                        </span>
                      </div>

                      <FormGroup className="d-flex flex-column align-items-center mb-0">
                        <Label
                          className="text-center"
                          style={{
                            fontSize: "19px",
                            fontWeight: 700,
                            color: isDark ? "#fff" : "#111827",
                            marginBottom: "24px",
                            lineHeight: 1.35,
                          }}
                        >
                          {question.questionText}
                        </Label>
                        {question.questionType === "multiple choice" && (
                          <div
                            className="d-flex flex-column gap-2 mx-auto text-start"
                            style={{ maxWidth: "420px", width: "100%" }}
                          >
                            {question.options
                              .filter(
                                option =>
                                  typeof option === "string" &&
                                  option.trim() !== "",
                              )
                              .map((option, optionIndex) => {
                                const selected = responses[responseKey] === option
                                return (
                                  <label
                                    key={optionIndex}
                                    className="survey-option-card"
                                    data-selected={selected}
                                  >
                                    <input
                                      type="radio"
                                      hidden
                                      name={`question${categoryIndex}_${questionIndex}`}
                                      value={option}
                                      checked={selected}
                                      onChange={() =>
                                        handleInputChange(
                                          categoryIndex,
                                          questionIndex,
                                          option,
                                        )
                                      }
                                    />
                                    <span className="survey-option-radio" />
                                    <span>{option}</span>
                                  </label>
                                )
                              })}
                          </div>
                        )}
                        {question.questionType === "text" && (
                          <input
                            type="text"
                            className="survey-text-input"
                            placeholder="Type your answer here..."
                            value={responses[responseKey] || ""}
                            onChange={e =>
                              handleInputChange(
                                categoryIndex,
                                questionIndex,
                                e.target.value,
                              )
                            }
                          />
                        )}
                        {question.questionType === "rating" &&
                          (() => {
                            const currentValue = responses[responseKey] ?? 0

                            return (
                              <div className="d-flex justify-content-center flex-wrap gap-2">
                                {[0, 1, 2, 3, 4, 5].map(n => (
                                  <button
                                    key={n}
                                    type="button"
                                    className="survey-rating-btn"
                                    data-selected={currentValue === n}
                                    onClick={() =>
                                      handleInputChange(
                                        categoryIndex,
                                        questionIndex,
                                        n,
                                      )
                                    }
                                  >
                                    {n}
                                  </button>
                                ))}
                              </div>
                            )
                          })()}
                      </FormGroup>
                    </CardBody>
                  </Card>
                )
              })()}

            <div style={{ height: "90px" }} />
          </>
        )}
      </div>
      <Modal
        isOpen={reviewRedirectionModalOpen}
        toggle={toggleReviewRedirectionModal}
        size="lg"
      >
        <ModalHeader toggle={toggleReviewRedirectionModal}>
          Please give us a review on the platforms below. We appreciate it!
        </ModalHeader>
        <ModalBody>
          <CardText className="text-muted text-center">
            Suggested review text (auto-copied when you choose a platform):
          </CardText>
          <Input
            type="textarea"
            rows={3}
            value={reviewText}
            readOnly
            className="mb-3"
            placeholder="Preset review text will appear here."
          />
          <CardText className="text-center">
            {reviewText ? reviewText : "Preset review text will appear here."}
          </CardText>
          <Row className="text-center justify-content-center">
            {/* Google Integration */}
            {googleReviewUrl && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() => handlePlatformClick(googleReviewUrl)}
                >
                  <div className="review-platform">
                    <img
                      src={googleIcon}
                      alt="Google"
                      style={{
                        height: "50px",
                        objectFit: "contain",
                      }}
                    />
                    <p style={{ marginTop: "10px", color: "#4285F4" }}>
                      Google
                    </p>
                  </div>
                </button>
              </Col>
            )}
            {/* Facebook Integration */}
            {organization?.FacebookIntegration && facebookReviewUrl && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() => handlePlatformClick(facebookReviewUrl)}
                >
                  <div className="review-platform">
                    <i
                      className="mdi mdi-facebook"
                      style={{
                        fontSize: "50px",
                        color: "#3b5998",
                      }}
                    ></i>
                    <p style={{ marginTop: "10px", color: "#3b5998" }}>
                      Facebook
                    </p>
                  </div>
                </button>
              </Col>
            )}

            {/* Yelp Integration */}
            {organization?.YelpIntegration && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() =>
                    handlePlatformClick(organization?.YelpIntegration.profileUrl)
                  }
                >
                  <div className="review-platform">
                    <img
                      src={yelpIcon}
                      alt="Yelp"
                      style={{
                        height: "50px",
                      }}
                    />
                    <p style={{ marginTop: "10px", color: "#d32323" }}>Yelp</p>
                  </div>
                </button>
              </Col>
            )}

            {/* Healthgrades Integration */}
            {organization?.HealthgradeIntegration && (
              <Col xs={6} md={4} className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 border-0"
                  onClick={() =>
                    handlePlatformClick(
                      organization?.HealthgradeIntegration.profileUrl,
                    )
                  }
                >
                  <div className="review-platform">
                    <img
                      src={healthGradeIcon}
                      alt="Healthgrades"
                      style={{
                        height: "50px",
                      }}
                    />
                    <p style={{ marginTop: "10px", color: "#007bb5" }}>
                      Healthgrades
                    </p>
                  </div>
                </button>
              </Col>
            )}
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          <Button color="secondary" onClick={toggleReviewRedirectionModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <GoogleReviewModal
        open={googleReviewModalOpen}
        toggle={setGoogleReviewModalOpen}
        url={googleReviewUrl}
        presetText={reviewText || ""}
        // openMode="popup" // optional: 'popup' | 'newtab' | 'sametab'
      />
      <textarea
        ref={hiddenTextareaRef}
        style={{ position: "absolute", left: -9999, top: -9999, opacity: 0 }}
        readOnly
      />
      <SnackbarComponent
        err={null}
        setErr={() => {}}
        success={copySuccess}
        setSuccess={setCopySuccess}
      />

      {/* Floating progress + Back/Next/Submit bar */}
      {started && !submitted && !loading && survey && flatQuestions.length > 0 && (
        (() => {
          const totalQuestions = flatQuestions.length
          const answeredQuestions = Object.keys(responses).filter(
            k => responses[k] !== "" && responses[k] !== null && responses[k] !== undefined
          ).length
          const allAnswered = answeredQuestions === totalQuestions
          const unansweredCount = totalQuestions - answeredQuestions
          const progressPct = Math.round(((currentIndex + 1) / totalQuestions) * 100)

          return (
            <div className="survey-progress-bar">
              {/* Progress info */}
              <div className="survey-progress-info" style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
                    Question {currentIndex + 1} of {totalQuestions}
                  </span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#2990DF", whiteSpace: "nowrap" }}>
                    {progressPct}%
                  </span>
                </div>
                <div style={{ height: "6px", backgroundColor: "#e5e7eb", borderRadius: "999px", overflow: "hidden" }}>
                  <div style={{
                    height: "100%",
                    width: `${progressPct}%`,
                    backgroundColor: "#2990DF",
                    borderRadius: "999px",
                    transition: "width 0.3s ease",
                  }} />
                </div>
                {isLastQuestion && !allAnswered && (
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#d97706",
                      marginTop: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {unansweredCount} unanswered
                  </div>
                )}
              </div>

              {/* Back / Next / Submit */}
              <div className="survey-progress-nav">
                <button
                  type="button"
                  className="survey-nav-back"
                  onClick={goToPreviousQuestion}
                  disabled={currentIndex === 0}
                >
                  Back
                </button>

                {isLastQuestion ? (
                  <button
                    type="button"
                    className={`survey-progress-submit${allAnswered ? " survey-progress-submit--ready" : ""}`}
                    onClick={allAnswered ? handleSubmit : undefined}
                    disabled={!allAnswered}
                    title={
                      allAnswered
                        ? undefined
                        : "Answer every question to submit"
                    }
                  >
                    Submit Survey
                  </button>
                ) : (
                  <button
                    type="button"
                    className="survey-progress-submit"
                    onClick={goToNextQuestion}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          )
        })()
      )}
    </div>
  )
}

export default SurveyForm
